import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import SectionHeading from '@/components/base/SectionHeading';
import MovieCard from '@/components/base/MovieCard';
import MovieListItem from '@/components/base/MovieListItem';
import EmptyState from '@/components/base/EmptyState';
import HeroCarousel from './components/HeroCarousel';
import CategoryGrid from './components/CategoryGrid';
import { getFeaturedMovies, getLatestMovies, getHotMovies } from '@/utils/movie';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function Home() {
  usePageMeta({
    title: '高清电影-最新电影资源与高清影片推荐',
    description: '高清电影网站提供最新电影资源、高清影片推荐和丰富的电影资讯，覆盖多类型高清电影，满足用户查找和观看高清电影的需求。',
    keywords: '高清电影,高清影片,最新电影,在线电影,电影推荐,高清影视,电影下载,电影资讯',
  });

  const featured = getFeaturedMovies(5);
  const latest = getLatestMovies(20);
  const hot = getHotMovies(10);

  return (
    <PageLayout>
      <HeroCarousel movies={featured} />

      <div className="w-full px-4 py-12 md:px-8 md:py-16">
        {/* Category quick entries */}
        <section className="mb-14">
          <SectionHeading icon="ri-apps-2-line" title="热门分类" subtitle="按类型快速找到你想看的电影" moreTo="/category/action" moreText="全部分类" />
          <CategoryGrid />
        </section>

        {/* Latest movies */}
        <section className="mb-14">
          <SectionHeading icon="ri-time-line" title="最新收录" subtitle="刚刚加入片库的 20 部高清电影" moreTo="/category/drama" moreText="浏览更多" />
          {latest.length === 0 ? (
            <EmptyState title="暂无相关电影，敬请期待" description="我们正在持续更新片库，请稍后再来看看。" />
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {latest.map((m) => (
                <MovieListItem key={m.id} movie={m} />
              ))}
            </div>
          )}
        </section>

        {/* Hot recommendations */}
        <section className="mb-14">
          <SectionHeading icon="ri-fire-line" title="热门推荐" subtitle="近期浏览热度最高的 10 部电影" moreTo="/search" moreText="去搜索" />
          {hot.length === 0 ? (
            <EmptyState title="暂无相关电影，敬请期待" description="热门榜单正在生成中。" />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 md:gap-4">
              {hot.map((m, i) => (
                <MovieCard key={m.id} movie={m} rank={i + 1} />
              ))}
            </div>
          )}
        </section>

        {/* Intro */}
        <section className="overflow-hidden rounded-lg bg-primary-900">
          <div className="grid grid-cols-1 gap-8 px-6 py-10 md:grid-cols-2 md:px-10 md:py-12">
            <div>
              <h2 className="font-heading text-2xl font-black text-white md:text-3xl">
                关于「高清电影」
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-background-300">
                高清电影是一个面向影迷的电影内容分享与推荐平台。我们汇聚动作、喜剧、剧情、科幻、爱情等多种类型的高清电影，提供清晰的分类浏览、详细的影片资料与贴心的相关推荐，帮助你用更少的时间，发现更值得看的电影。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-background-300">
                本站所有影片信息与数据均为演示内容，旨在展示电影门户网站的使用体验，不提供真实播放与下载服务。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-accent-600 cursor-pointer"
                >
                  <i className="ri-information-line text-lg"></i>
                  了解更多
                </Link>
                <Link
                  to="/search"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-white/30 bg-white/10 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-white/20 cursor-pointer"
                >
                  <i className="ri-search-line text-lg"></i>
                  开始搜索
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 self-center">
              {[
                { num: `${latest.length}+`, label: '最新收录', icon: 'ri-film-line' },
                { num: '8', label: '电影分类', icon: 'ri-apps-2-line' },
                { num: '100%', label: '免费浏览', icon: 'ri-shield-check-line' },
                { num: '4K', label: '高清画质', icon: 'ri-4k-line' },
              ].map((s) => (
                <div key={s.label} className="rounded-lg border border-primary-700 bg-primary-800/60 p-5">
                  <i className={`${s.icon} text-2xl text-accent-400`}></i>
                  <p className="mt-3 font-heading text-2xl font-black text-white">{s.num}</p>
                  <p className="mt-1 text-xs text-background-300">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}